import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { heroData, statsData } from '../data/mock';
import { ArrowRight, Play, Zap, Target, Users } from 'lucide-react';
import { Link } from 'react-router-dom';


const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center relative overflow-hidden pt-20 bg-white">
        <div className="absolute inset-0 grid-bg" />
        <div className="absolute top-1/4 -left-40 w-[600px] h-[600px] bg-neutral-100 rounded-full blur-[150px] opacity-60" />
        <div className="absolute bottom-1/4 -right-40 w-[600px] h-[600px] bg-neutral-100 rounded-full blur-[150px] opacity-60" />

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
          
            <div className="inline-flex items-center gap-2 bg-neutral-100 border border-neutral-200 rounded-full px-5 py-2 mb-10">
              <span className="w-1.5 h-1.5 bg-black rounded-full" />
              <span className="text-neutral-600 text-sm font-medium tracking-wide">
                {heroData.tagline}
              </span>
            </div>

            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-black leading-[0.95] mb-8 tracking-tight">
              Hire Smarter.
              <br />
              <span className="text-neutral-400">Effortlessly.</span>
            </h1>

            <p className="text-lg md:text-xl text-neutral-500 max-w-2xl mx-auto mb-12 leading-relaxed">
              {heroData.subheadline}
            </p>

          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section bg-neutral-50 relative">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">
                The Sandgen Philosophy
              </span>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-black leading-[1] mb-6 tracking-tight">
                We don&apos;t do
                <br />
                <span className="text-neutral-400">ordinary.</span>
              </h2>
            </div>
            <div>
              <p className="text-lg text-neutral-600 leading-relaxed mb-6">
                While others build tools, we craft experiences. Sandgen isn&apos;t just software—it&apos;s
                the unfair advantage your competitors wish they had.
              </p>
              <p className="text-lg text-neutral-600 leading-relaxed">
                Our neural intelligence doesn&apos;t just match keywords. It understands nuance,
                context, and potential. It sees what humans miss and finds what others can&apos;t.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white border border-neutral-200 p-10 group hover:border-neutral-300 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-neutral-100 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-black group-hover:text-white transition-all duration-300">
                <Zap className="w-7 h-7" />
              </div>
              <h3 className="text-black text-xl font-semibold mb-4">Velocity Without Compromise</h3>
              <p className="text-neutral-500 leading-relaxed">
                87% faster than traditional methods. Because waiting is for people without Sandgen.
              </p>
            </div>

            <div className="bg-white border border-neutral-200 p-10 group hover:border-neutral-300 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-neutral-100 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-black group-hover:text-white transition-all duration-300">
                <Target className="w-7 h-7" />
              </div>
              <h3 className="text-black text-xl font-semibold mb-4">Surgical Precision</h3>
              <p className="text-neutral-500 leading-relaxed">
                3.2x better match rates. Our intelligence doesn&apos;t guess—it knows.
              </p>
            </div>

            <div className="bg-white border border-neutral-200 p-10 group hover:border-neutral-300 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-neutral-100 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-black group-hover:text-white transition-all duration-300">
                <Users className="w-7 h-7" />
              </div>
              <h3 className="text-black text-xl font-semibold mb-4">Human-Amplified</h3>
              <p className="text-neutral-500 leading-relaxed">
                AI that elevates judgment, not replaces it. You&apos;re still the genius. We just help.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Explore Section */}
      <section className="section bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl text-black leading-[1] mb-6 tracking-tight">
              The Complete
              <br />
              <span className="text-neutral-400">Sandgen Suite</span>
            </h2>
            <p className="text-lg text-neutral-500 max-w-2xl mx-auto">
              Every tool you need. Nothing you don&apos;t. Elegantly orchestrated.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Suite', desc: 'Core capabilities, perfected', link: '/features' },
              { title: 'Assessments', desc: 'Assessments that actually assess', link: '/assessments' },
              { title: 'Intelligence', desc: 'AI that thinks like you do', link: '/ai-matching' },
              { title: 'Interviews', desc: 'Where great interviews happen', link: '/interviews' },
            ].map((item, index) => (
              <Link
                key={index}
                to={item.link}
                className="bg-neutral-50 border border-neutral-100 p-8 group hover:bg-white hover:border-neutral-200 hover:shadow-xl transition-all duration-300"
              >
                <h3 className="text-black text-xl font-semibold mb-2 group-hover:text-neutral-700">{item.title}</h3>
                <p className="text-neutral-500 mb-4">{item.desc}</p>
                <span className="inline-flex items-center gap-2 text-black font-medium text-sm group-hover:gap-3 transition-all">
                  Explore <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-black relative overflow-hidden">
        <div className="absolute inset-0 grid-bg-dark" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[1] mb-6 tracking-tight">
              Ready to hire like you mean it?
            </h2>
            <p className="text-lg text-neutral-400 mb-10">
              500+ companies already know the difference. Time you did too.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link target='_blank' to="https://app.sandgen.com/admin/login" className="bg-white text-black px-8 py-4 rounded-full font-medium hover:bg-neutral-200 transition-colors inline-flex items-center gap-2">
                Experience Sandgen
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
